import {useCallback, useEffect} from 'react';
import {debounce} from 'lodash';
import {ImageSource} from 'mapbox-gl';

import {MapRef} from '~types/Map';
import {useMapIsLoaded} from '~utils/map/events/useMapLoadedEffect';
import {MapLayer, MapSource} from '~constants/plots';
import {hexToRectangle} from '~utils/plot/coordinates';
import {PlotCoordinate} from '~types/Plots';

import {mapAddLayerIfNotExist, mapAddSourceIfNotExist} from './layer';

const resolutionsImages = {
    high: 'https://img.plots.everdome.io/map-lod/high.png',
    medium: 'https://img.plots.everdome.io/map-lod/medium.png',
    low: 'https://img.plots.everdome.io/map-lod/low.png',
};

const mapBbox = [
    [77.434347, 18.459207],
    [77.460538, 18.484116999999998],
];

export default function useMapPlotsMockedImagesLayers(mapRef: MapRef) {
    const loaded = useMapIsLoaded(mapRef);
    const hexFillLoaded = Boolean(mapRef.current?.getLayer(MapLayer.HexFill));

    const addLayers = useCallback(
        debounce(() => {
            if (!mapRef.current) return;

            const currentZoom = mapRef.current.getZoom();

            if (currentZoom > 17.5) return;

            const resolution =
                currentZoom > 15 ? (currentZoom > 16 ? 'high' : 'medium') : 'low';

            const imageSource = mapRef.current.getSource(
                MapSource.MockedMap,
            ) as ImageSource;

            if (imageSource && mapRef.current.getLayer(MapLayer.MockedMap)) {
                //@ts-ignore
                if (imageSource.url !== resolutionsImages[resolution]) {
                    imageSource.updateImage({url: resolutionsImages[resolution]});
                }
            } else {
                mapAddSourceIfNotExist(mapRef.current, MapSource.MockedMap, {
                    type: 'image',
                    url: resolutionsImages[resolution],
                    coordinates: hexToRectangle(mapBbox as PlotCoordinate[]),
                });

                mapAddLayerIfNotExist(
                    mapRef.current,
                    {
                        id: MapLayer.MockedMap,
                        source: MapSource.MockedMap,
                        type: 'raster',
                        minzoom: 11,
                        maxzoom: 17.5,
                    },
                    MapLayer.HexFill,
                );
            }
        }, 500),
        [mapRef.current],
    );

    useEffect(() => {
        if (loaded && hexFillLoaded) {
            addLayers();
            mapRef.current?.on('moveend', addLayers);

            return () => {
                mapRef.current?.off('moveend', addLayers);
            };
        }
    }, [loaded, hexFillLoaded]);
}
